// src/components/admin/nornsweave/YearSetupTab.js
import React, { useState } from 'react';
import { useAdminNornsWeave } from './AdminNornsWeaveContext';
import { formatTime12 } from '../../TimePicker';
import { useAuth } from '../../../contexts/AuthContext';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';

function YearSetupTab({
  // Handlers
  onToggleLock,
  onStartWizard,
  onFixWorkdays,
  onCleanupDuplicates,
  onClearAllData,
  onAddHolyDay,
  onDeleteHolyDay,
  onEditHolyDay,
  onAddAdorationDay,
  onDeleteAdorationDay,
  onEditAdorationDay,
  onAddSchoolCalendarEvent,
  onDeleteSchoolCalendarEvent,
  onEditSchoolCalendarEvent,
  onAddSaturdayOpening,
  onDeleteSaturdayOpening,
  onEditSaturdayOpening,
  // Saving states
  savingHolyDay,
  savingAdorationDay,
  savingSchoolCalendarEvent
}) {
  const {
    settings,
    holyDays,
    adorationDays,
    schoolCalendarEvents,
    saturdayOpenings,
    isYearSetupLocked,
    yearSetupForm,
    getDayOfWeekFromDate,
    loading,
    saturdaySchedule,
    updateSaturdaySchedule,
    setYearSetupForm
  } = useAdminNornsWeave();

  const { currentUser } = useAuth();

  // Check if year is configured (any of these indicates setup is complete)
  // Use yearSetupForm as the primary source of truth since it's populated from settings
  // Also check if any year setup data exists (holy days, adoration days, etc.)
  const hasYearSetupData = holyDays.length > 0 ||
    adorationDays.length > 0 ||
    schoolCalendarEvents.length > 0;

  const isYearConfigured = yearSetupForm.academicYearStart ||
    settings?.academicYear?.startDate ||
    settings?.academicYear?.isSetupComplete ||
    hasYearSetupData;

  // Inline form states
  const [showAddHolyDayForm, setShowAddHolyDayForm] = useState(false);
  const [showAddAdorationForm, setShowAddAdorationForm] = useState(false);
  const [showAddHolidayForm, setShowAddHolidayForm] = useState(false);
  const [showAddHalfDayForm, setShowAddHalfDayForm] = useState(false);
  const [showAddEventForm, setShowAddEventForm] = useState(false);

  const [newHolyDayForm, setNewHolyDayForm] = useState({ name: '', date: '' });
  const [newAdorationForm, setNewAdorationForm] = useState({ date: '' });
  const [newHolidayForm, setNewHolidayForm] = useState({ name: '', startDate: '', endDate: '' });
  const [newHalfDayForm, setNewHalfDayForm] = useState({ name: '', date: '', period: 'afternoon' });
  const [newEventForm, setNewEventForm] = useState({ type: 'book-fair', name: '', startDate: '', endDate: '', libraryStatus: 'closed' });

  // Edit states - store the ID of item being edited
  const [editingHolyDay, setEditingHolyDay] = useState(null);
  const [editingAdoration, setEditingAdoration] = useState(null);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [editingHalfDay, setEditingHalfDay] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);

  // Edit form states
  const [editHolyDayForm, setEditHolyDayForm] = useState({ name: '', date: '' });
  const [editAdorationForm, setEditAdorationForm] = useState({ date: '' });
  const [editHolidayForm, setEditHolidayForm] = useState({ name: '', startDate: '', endDate: '' });
  const [editHalfDayForm, setEditHalfDayForm] = useState({ name: '', date: '', period: 'afternoon' });
  const [editEventForm, setEditEventForm] = useState({ type: 'book-fair', name: '', startDate: '', endDate: '', libraryStatus: 'closed' });

  // Collapsible section states (default collapsed)
  const [expandedSections, setExpandedSections] = useState({
    holyDays: false,
    adoration: false,
    holidays: false,
    halfDays: false,
    libraryEvents: false,
    saturdayOpenings: false
  });

  // Saturday editing states
  const [editingSaturdayDate, setEditingSaturdayDate] = useState(null);
  const [editSaturdayForm, setEditSaturdayForm] = useState({ date: '', description: '' });
  const [showAddSaturdayForm, setShowAddSaturdayForm] = useState(false);
  const [newSaturdayForm, setNewSaturdayForm] = useState({ date: '', description: '' });
  const [showSaturdayConfigForm, setShowSaturdayConfigForm] = useState(false);
  const [saturdayConfigForm, setSaturdayConfigForm] = useState({
    enabled: true,
    startTime: '09:00',
    endTime: '13:00'
  });

  // Year dates configuration
  const [showYearDatesForm, setShowYearDatesForm] = useState(false);
  const [yearDatesConfigForm, setYearDatesConfigForm] = useState({
    academicYearStart: yearSetupForm.academicYearStart || '',
    academicYearEnd: yearSetupForm.academicYearEnd || '',
    schoolStartDate: yearSetupForm.schoolStartDate || '',
    schoolEndDate: yearSetupForm.schoolEndDate || '',
    massDay: yearSetupForm.massDay || 'Thursday',
    massStartTime: yearSetupForm.massStartTime || '09:00',
    massEndTime: yearSetupForm.massEndTime || '10:00'
  });

  // Admin tools toggle (hidden by default)
  const [showAdminTools, setShowAdminTools] = useState(false);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Show loading state while data is being fetched
  // Also check if settings is still null (data hasn't been populated yet)
  if (loading || settings === null) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="text-center py-12">
          <div className="text-4xl mb-4 animate-pulse">📅</div>
          <p className="text-slate-500">Loading academic year data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div
          className="cursor-pointer"
          onDoubleClick={() => setShowAdminTools(!showAdminTools)}
          title="Double-click for admin tools"
        >
          <h2 className="text-2xl font-bold text-slate-800">Academic Year Setup</h2>
          <p className="text-slate-600 text-sm mt-1">
            Configure your school calendar, mass schedule, and closures
          </p>
        </div>
        <div className="flex gap-3">
          {showAdminTools && (
            <>
              <button
                onClick={onFixWorkdays}
                className="bg-orange-100 text-orange-700 px-4 py-2 rounded-lg hover:bg-orange-200 transition-colors font-medium border border-orange-300"
                title="Fix workdays that incorrectly show as Library Closed"
              >
                Fix Workdays
              </button>
              <button
                onClick={onCleanupDuplicates}
                className="bg-red-100 text-red-700 px-4 py-2 rounded-lg hover:bg-red-200 transition-colors font-medium border border-red-300"
                title="Find and remove duplicate entries"
              >
                Clean Duplicates
              </button>
              <button
                onClick={onClearAllData}
                className="bg-rose-100 text-rose-800 px-4 py-2 rounded-lg hover:bg-rose-200 transition-colors font-medium border border-rose-400"
                title="⚠️ Delete ALL calendar data - cannot be undone!"
              >
                Clear All Data
              </button>
            </>
          )}
          {isYearConfigured ? (
            <>
              <button
                onClick={onToggleLock}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  isYearSetupLocked
                    ? 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                    : 'bg-yellow-500 text-white hover:bg-yellow-600'
                }`}
              >
                {isYearSetupLocked ? 'Unlock to Edit' : 'Lock Calendar'}
              </button>
              <button
                onClick={onStartWizard}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                + Set Up New Year
              </button>
            </>
          ) : (
            <button
              onClick={onStartWizard}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg"
            >
              Start Year Setup Wizard
            </button>
          )}
        </div>
      </div>

      {/* Welcome message if no year configured */}
      {!isYearConfigured && (
        <div className="text-center py-12 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200">
          <div className="text-6xl mb-4">📅</div>
          <h3 className="text-xl font-bold text-blue-800 mb-2">Welcome! Let's set up your academic year</h3>
          <p className="text-blue-600 max-w-md mx-auto">
            The setup wizard will guide you through configuring your school year dates,
            mass schedule, holy days, adoration days, and holidays.
          </p>
        </div>
      )}

      {/* Year overview if configured */}
      {isYearConfigured && (
        <div className="space-y-6">
          {/* Academic Year Header */}
          {!showYearDatesForm ? (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-bold text-blue-800">
                    Academic Year {yearSetupForm.academicYearStart?.split('-')[0]}-{yearSetupForm.academicYearEnd?.split('-')[0]}
                  </h3>
                  <div className="space-y-0.5">
                    <p className="text-blue-600 text-sm">
                      <span className="font-medium">Year:</span> {yearSetupForm.academicYearStart && new Date(yearSetupForm.academicYearStart + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {yearSetupForm.academicYearEnd && new Date(yearSetupForm.academicYearEnd + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                    {(yearSetupForm.schoolStartDate || yearSetupForm.schoolEndDate) && (
                      <p className="text-green-600 text-sm">
                        <span className="font-medium">School Days:</span> {yearSetupForm.schoolStartDate && new Date(yearSetupForm.schoolStartDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {yearSetupForm.schoolEndDate && new Date(yearSetupForm.schoolEndDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-sm text-blue-600">Regular Mass Day</p>
                    <p className="font-bold text-blue-800">
                      {yearSetupForm.massDay}s, {formatTime12(yearSetupForm.massStartTime)} - {formatTime12(yearSetupForm.massEndTime)}
                    </p>
                  </div>
                  {!isYearSetupLocked && (
                    <button
                      onClick={() => {
                        setYearDatesConfigForm({
                          academicYearStart: yearSetupForm.academicYearStart || '',
                          academicYearEnd: yearSetupForm.academicYearEnd || '',
                          schoolStartDate: yearSetupForm.schoolStartDate || '',
                          schoolEndDate: yearSetupForm.schoolEndDate || '',
                          massDay: yearSetupForm.massDay || 'Thursday',
                          massStartTime: yearSetupForm.massStartTime || '09:00',
                          massEndTime: yearSetupForm.massEndTime || '10:00'
                        });
                        setShowYearDatesForm(true);
                      }}
                      className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap"
                    >
                      Edit Dates
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
              <h3 className="text-lg font-bold text-blue-800 mb-4">Configure Academic Year Dates</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">Academic Year Start</label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none"
                      value={yearDatesConfigForm.academicYearStart}
                      onChange={(e) => setYearDatesConfigForm({ ...yearDatesConfigForm, academicYearStart: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">Academic Year End</label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none"
                      value={yearDatesConfigForm.academicYearEnd}
                      onChange={(e) => setYearDatesConfigForm({ ...yearDatesConfigForm, academicYearEnd: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-green-700 mb-1">School Start Date</label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-green-200 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 outline-none"
                      value={yearDatesConfigForm.schoolStartDate}
                      onChange={(e) => setYearDatesConfigForm({ ...yearDatesConfigForm, schoolStartDate: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-green-700 mb-1">School End Date</label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-green-200 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 outline-none"
                      value={yearDatesConfigForm.schoolEndDate}
                      onChange={(e) => setYearDatesConfigForm({ ...yearDatesConfigForm, schoolEndDate: e.target.value })}
                    />
                  </div>
                </div>
                <div className="border-t border-blue-200 pt-4">
                  <h4 className="font-medium text-blue-800 mb-3">Regular Mass Schedule</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-1">Day of Week</label>
                      <select
                        className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none"
                        value={yearDatesConfigForm.massDay}
                        onChange={(e) => setYearDatesConfigForm({ ...yearDatesConfigForm, massDay: e.target.value })}
                      >
                        <option value="Monday">Monday</option>
                        <option value="Tuesday">Tuesday</option>
                        <option value="Wednesday">Wednesday</option>
                        <option value="Thursday">Thursday</option>
                        <option value="Friday">Friday</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-1">Start Time</label>
                      <input
                        type="time"
                        className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none"
                        value={yearDatesConfigForm.massStartTime}
                        onChange={(e) => setYearDatesConfigForm({ ...yearDatesConfigForm, massStartTime: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-1">End Time</label>
                      <input
                        type="time"
                        className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none"
                        value={yearDatesConfigForm.massEndTime}
                        onChange={(e) => setYearDatesConfigForm({ ...yearDatesConfigForm, massEndTime: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={async () => {
                      if (!currentUser) return;

                      try {
                        const settingsRef = doc(db, 'users', currentUser.uid, 'settings', 'nornsWeave');
                        const existingDoc = await getDoc(settingsRef);
                        const existingSettings = existingDoc.exists() ? existingDoc.data() : {};

                        const updatedData = {
                          ...existingSettings,
                          academicYear: {
                            ...existingSettings.academicYear,
                            startDate: yearDatesConfigForm.academicYearStart,
                            endDate: yearDatesConfigForm.academicYearEnd,
                            schoolStartDate: yearDatesConfigForm.schoolStartDate,
                            schoolEndDate: yearDatesConfigForm.schoolEndDate,
                            lastModifiedAt: new Date().toISOString()
                          },
                          massSchedule: {
                            regularDay: yearDatesConfigForm.massDay,
                            startTime: yearDatesConfigForm.massStartTime,
                            endTime: yearDatesConfigForm.massEndTime
                          }
                        };

                        await setDoc(settingsRef, updatedData, { merge: true });

                        // Update local state
                        setYearSetupForm({
                          ...yearSetupForm,
                          academicYearStart: yearDatesConfigForm.academicYearStart,
                          academicYearEnd: yearDatesConfigForm.academicYearEnd,
                          schoolStartDate: yearDatesConfigForm.schoolStartDate,
                          schoolEndDate: yearDatesConfigForm.schoolEndDate,
                          massDay: yearDatesConfigForm.massDay,
                          massStartTime: yearDatesConfigForm.massStartTime,
                          massEndTime: yearDatesConfigForm.massEndTime
                        });

                        setShowYearDatesForm(false);
                        alert('Year dates updated successfully!');
                      } catch (error) {
                        console.error('Error saving year dates:', error);
                        alert('Failed to save year dates. Please try again.');
                      }
                    }}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Save Year Dates
                  </button>
                  <button
                    onClick={() => setShowYearDatesForm(false)}
                    className="text-blue-600 hover:text-blue-800 px-4"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Holy Days Section */}
          <div className="border-2 border-purple-200 rounded-xl overflow-hidden">
            <div
              className="bg-purple-50 px-4 py-3 flex justify-between items-center cursor-pointer hover:bg-purple-100 transition-colors"
              onClick={() => toggleSection('holyDays')}
            >
              <h4 className="font-bold text-purple-800 flex items-center gap-2">
                <span className={`transition-transform ${expandedSections.holyDays ? 'rotate-90' : ''}`}>▶</span>
                <span>⛪</span> Holy Day Exceptions
                <span className="text-sm font-normal text-purple-600">({holyDays.length})</span>
              </h4>
              {!isYearSetupLocked && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowAddHolyDayForm(true); toggleSection('holyDays'); }}
                  className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                >
                  + Add Holy Day
                </button>
              )}
            </div>
            {expandedSections.holyDays && <div className="p-4">
              {showAddHolyDayForm && (
                <div className="bg-purple-50 rounded-lg p-3 mb-3 border border-purple-200">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Holy Day Name"
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      value={newHolyDayForm.name}
                      onChange={(e) => setNewHolyDayForm({...newHolyDayForm, name: e.target.value})}
                    />
                    <input
                      type="date"
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      value={newHolyDayForm.date}
                      onChange={(e) => setNewHolyDayForm({...newHolyDayForm, date: e.target.value})}
                    />
                    <button
                      onClick={async () => {
                        if (newHolyDayForm.name && newHolyDayForm.date) {
                          await onAddHolyDay(newHolyDayForm.name, newHolyDayForm.date);
                          if (!savingHolyDay) {
                            setNewHolyDayForm({ name: '', date: '' });
                            setShowAddHolyDayForm(false);
                          }
                        }
                      }}
                      disabled={savingHolyDay}
                      className={`px-3 py-2 rounded-lg text-sm ${savingHolyDay ? 'bg-slate-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'} text-white`}
                    >
                      {savingHolyDay ? 'Saving...' : 'Add'}
                    </button>
                    <button
                      onClick={() => { setShowAddHolyDayForm(false); setNewHolyDayForm({ name: '', date: '' }); }}
                      className="text-slate-500 hover:text-slate-700"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}
              {holyDays.length === 0 && !showAddHolyDayForm ? (
                <p className="text-slate-500 text-sm text-center py-4">No holy days configured</p>
              ) : (
                <div className="space-y-2">
                  {holyDays.sort((a, b) => new Date(a.date) - new Date(b.date)).map(hd => (
                    editingHolyDay === hd.id ? (
                      <div key={hd.id} className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            placeholder="Holy Day Name"
                            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            value={editHolyDayForm.name}
                            onChange={(e) => setEditHolyDayForm({...editHolyDayForm, name: e.target.value})}
                          />
                          <input
                            type="date"
                            className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            value={editHolyDayForm.date}
                            onChange={(e) => setEditHolyDayForm({...editHolyDayForm, date: e.target.value})}
                          />
                          <button
                            onClick={async () => {
                              if (editHolyDayForm.name && editHolyDayForm.date && onEditHolyDay) {
                                await onEditHolyDay(hd.id, editHolyDayForm.name, editHolyDayForm.date);
                                setEditingHolyDay(null);
                              }
                            }}
                            disabled={savingHolyDay}
                            className={`px-3 py-2 rounded-lg text-sm ${savingHolyDay ? 'bg-slate-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'} text-white`}
                          >
                            {savingHolyDay ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingHolyDay(null)}
                            className="text-slate-500 hover:text-slate-700"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div key={hd.id} className="flex justify-between items-center bg-white rounded-lg px-3 py-2 border border-purple-100 group">
                        <div>
                          <span className="font-medium text-slate-800">{hd.name}</span>
                          <span className="text-slate-500 text-sm ml-2">
                            {new Date(hd.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            hd.shiftsMass ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {hd.shiftsMass ? 'Mass shifts' : 'No shift'}
                          </span>
                          {!isYearSetupLocked && (
                            <>
                              <button
                                onClick={() => {
                                  setEditHolyDayForm({ name: hd.name, date: hd.date });
                                  setEditingHolyDay(hd.id);
                                }}
                                className="text-purple-400 hover:text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                ✎
                              </button>
                              <button
                                onClick={() => onDeleteHolyDay(hd.id)}
                                className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                ✕
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )
                  ))}
                </div>
              )}
            </div>}
          </div>

          {/* Adoration Days Section */}
          <div className="border-2 border-indigo-200 rounded-xl overflow-hidden">
            <div
              className="bg-indigo-50 px-4 py-3 flex justify-between items-center cursor-pointer hover:bg-indigo-100 transition-colors"
              onClick={() => toggleSection('adoration')}
            >
              <h4 className="font-bold text-indigo-800 flex items-center gap-2">
                <span className={`transition-transform ${expandedSections.adoration ? 'rotate-90' : ''}`}>▶</span>
                <span>🙏</span> Adoration Days (Fridays)
                <span className="text-sm font-normal text-indigo-600">({adorationDays.length})</span>
              </h4>
              {!isYearSetupLocked && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowAddAdorationForm(true); toggleSection('adoration'); }}
                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                >
                  + Add Date
                </button>
              )}
            </div>
            {expandedSections.adoration && <div className="p-4">
              {showAddAdorationForm && (
                <div className="bg-indigo-50 rounded-lg p-3 mb-3 border border-indigo-200">
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      value={newAdorationForm.date}
                      onChange={(e) => setNewAdorationForm({...newAdorationForm, date: e.target.value})}
                    />
                    <span className="text-sm text-slate-500">
                      {newAdorationForm.date ? getDayOfWeekFromDate(newAdorationForm.date) : 'Select a Friday'}
                    </span>
                    <button
                      onClick={async () => {
                        if (newAdorationForm.date) {
                          await onAddAdorationDay(newAdorationForm.date);
                          if (!savingAdorationDay) {
                            setNewAdorationForm({ date: '' });
                            setShowAddAdorationForm(false);
                          }
                        }
                      }}
                      disabled={savingAdorationDay}
                      className={`px-3 py-2 rounded-lg text-sm ${savingAdorationDay ? 'bg-slate-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'} text-white`}
                    >
                      {savingAdorationDay ? 'Saving...' : 'Add'}
                    </button>
                    <button
                      onClick={() => { setShowAddAdorationForm(false); setNewAdorationForm({ date: '' }); }}
                      className="text-slate-500 hover:text-slate-700"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}
              {adorationDays.length === 0 && !showAddAdorationForm ? (
                <p className="text-slate-500 text-sm text-center py-4">No adoration days configured</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {adorationDays.sort((a, b) => new Date(a.date) - new Date(b.date)).map(ad => (
                    editingAdoration === ad.id ? (
                      <div key={ad.id} className="bg-indigo-50 rounded-lg p-2 border border-indigo-200 inline-flex items-center gap-2">
                        <input
                          type="date"
                          className="px-2 py-1 border border-slate-300 rounded text-sm"
                          value={editAdorationForm.date}
                          onChange={(e) => setEditAdorationForm({...editAdorationForm, date: e.target.value})}
                        />
                        <button
                          onClick={async () => {
                            if (editAdorationForm.date && onEditAdorationDay) {
                              await onEditAdorationDay(ad.id, editAdorationForm.date);
                              setEditingAdoration(null);
                            }
                          }}
                          disabled={savingAdorationDay}
                          className={`px-2 py-1 rounded text-xs ${savingAdorationDay ? 'bg-slate-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'} text-white`}
                        >
                          {savingAdorationDay ? '...' : 'Save'}
                        </button>
                        <button
                          onClick={() => setEditingAdoration(null)}
                          className="text-slate-500 hover:text-slate-700 text-sm"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <span key={ad.id} className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm font-medium group inline-flex items-center gap-1">
                        {new Date(ad.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        {!isYearSetupLocked && (
                          <>
                            <button
                              onClick={() => {
                                setEditAdorationForm({ date: ad.date });
                                setEditingAdoration(ad.id);
                              }}
                              className="text-indigo-400 hover:text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity ml-1"
                            >
                              ✎
                            </button>
                            <button
                              onClick={() => onDeleteAdorationDay(ad.id)}
                              className="text-indigo-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              ✕
                            </button>
                          </>
                        )}
                      </span>
                    )
                  ))}
                </div>
              )}
              <p className="text-xs text-indigo-500 mt-3">
                Same time as Mass ({formatTime12(yearSetupForm.massStartTime)} - {formatTime12(yearSetupForm.massEndTime)}) - Does not affect Thursday Mass
              </p>
            </div>}
          </div>

          {/* School Holidays Section */}
          <div className="border-2 border-red-200 rounded-xl overflow-hidden">
            <div
              className="bg-red-50 px-4 py-3 flex justify-between items-center cursor-pointer hover:bg-red-100 transition-colors"
              onClick={() => toggleSection('holidays')}
            >
              <h4 className="font-bold text-red-800 flex items-center gap-2">
                <span className={`transition-transform ${expandedSections.holidays ? 'rotate-90' : ''}`}>▶</span>
                <span>🎄</span> School Holidays
                <span className="text-sm font-normal text-red-600">({schoolCalendarEvents.filter(e => e.type === 'holiday').length})</span>
              </h4>
              {!isYearSetupLocked && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowAddHolidayForm(true); toggleSection('holidays'); }}
                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  + Add Holiday
                </button>
              )}
            </div>
            {expandedSections.holidays && <div className="p-4">
              {showAddHolidayForm && (
                <div className="bg-red-50 rounded-lg p-3 mb-3 border border-red-200">
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      type="text"
                      placeholder="Holiday Name"
                      className="flex-1 min-w-[120px] px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      value={newHolidayForm.name}
                      onChange={(e) => setNewHolidayForm({...newHolidayForm, name: e.target.value})}
                    />
                    <input
                      type="date"
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      value={newHolidayForm.startDate}
                      onChange={(e) => setNewHolidayForm({...newHolidayForm, startDate: e.target.value})}
                    />
                    <span className="text-slate-400">to</span>
                    <input
                      type="date"
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      value={newHolidayForm.endDate}
                      onChange={(e) => setNewHolidayForm({...newHolidayForm, endDate: e.target.value})}
                    />
                    <button
                      onClick={async () => {
                        if (newHolidayForm.name && newHolidayForm.startDate) {
                          await onAddSchoolCalendarEvent({
                            type: 'holiday',
                            name: newHolidayForm.name,
                            startDate: newHolidayForm.startDate,
                            endDate: newHolidayForm.endDate || newHolidayForm.startDate,
                            libraryStatus: 'closed'
                          });
                          if (!savingSchoolCalendarEvent) {
                            setNewHolidayForm({ name: '', startDate: '', endDate: '' });
                            setShowAddHolidayForm(false);
                          }
                        }
                      }}
                      disabled={savingSchoolCalendarEvent}
                      className={`px-3 py-2 rounded-lg text-sm ${savingSchoolCalendarEvent ? 'bg-slate-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'} text-white`}
                    >
                      {savingSchoolCalendarEvent ? 'Saving...' : 'Add'}
                    </button>
                    <button
                      onClick={() => { setShowAddHolidayForm(false); setNewHolidayForm({ name: '', startDate: '', endDate: '' }); }}
                      className="text-slate-500 hover:text-slate-700"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}
              {schoolCalendarEvents.filter(e => e.type === 'holiday').length === 0 && !showAddHolidayForm ? (
                <p className="text-slate-500 text-sm text-center py-4">No holidays configured</p>
              ) : (
                <div className="space-y-2">
                  {schoolCalendarEvents.filter(e => e.type === 'holiday').sort((a, b) => new Date(a.startDate) - new Date(b.startDate)).map(h => (
                    editingHoliday === h.id ? (
                      <div key={h.id} className="bg-red-50 rounded-lg p-3 border border-red-200">
                        <div className="flex items-center gap-2 flex-wrap">
                          <input
                            type="text"
                            placeholder="Holiday Name"
                            className="flex-1 min-w-[120px] px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            value={editHolidayForm.name}
                            onChange={(e) => setEditHolidayForm({...editHolidayForm, name: e.target.value})}
                          />
                          <input
                            type="date"
                            className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            value={editHolidayForm.startDate}
                            onChange={(e) => setEditHolidayForm({...editHolidayForm, startDate: e.target.value})}
                          />
                          <span className="text-slate-400">to</span>
                          <input
                            type="date"
                            className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            value={editHolidayForm.endDate}
                            onChange={(e) => setEditHolidayForm({...editHolidayForm, endDate: e.target.value})}
                          />
                          <button
                            onClick={async () => {
                              if (editHolidayForm.name && editHolidayForm.startDate && onEditSchoolCalendarEvent) {
                                await onEditSchoolCalendarEvent(h.id, {
                                  name: editHolidayForm.name,
                                  startDate: editHolidayForm.startDate,
                                  endDate: editHolidayForm.endDate || editHolidayForm.startDate
                                });
                                setEditingHoliday(null);
                              }
                            }}
                            disabled={savingSchoolCalendarEvent}
                            className={`px-3 py-2 rounded-lg text-sm ${savingSchoolCalendarEvent ? 'bg-slate-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'} text-white`}
                          >
                            {savingSchoolCalendarEvent ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingHoliday(null)}
                            className="text-slate-500 hover:text-slate-700"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div key={h.id} className="flex justify-between items-center bg-white rounded-lg px-3 py-2 border border-red-100 group">
                        <span className="font-medium text-slate-800">{h.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500 text-sm">
                            {h.startDate === h.endDate
                              ? new Date(h.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                              : `${new Date(h.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(h.endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                            }
                          </span>
                          {!isYearSetupLocked && (
                            <>
                              <button
                                onClick={() => {
                                  setEditHolidayForm({ name: h.name, startDate: h.startDate, endDate: h.endDate });
                                  setEditingHoliday(h.id);
                                }}
                                className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                ✎
                              </button>
                              <button
                                onClick={() => onDeleteSchoolCalendarEvent(h.id)}
                                className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                ✕
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )
                  ))}
                </div>
              )}
            </div>}
          </div>

          {/* Half Days Section */}
          <div className="border-2 border-orange-200 rounded-xl overflow-hidden">
            <div
              className="bg-orange-50 px-4 py-3 flex justify-between items-center cursor-pointer hover:bg-orange-100 transition-colors"
              onClick={() => toggleSection('halfDays')}
            >
              <h4 className="font-bold text-orange-800 flex items-center gap-2">
                <span className={`transition-transform ${expandedSections.halfDays ? 'rotate-90' : ''}`}>▶</span>
                <span>🕐</span> Half Days
                <span className="text-sm font-normal text-orange-600">({schoolCalendarEvents.filter(e => e.type === 'half-day').length})</span>
              </h4>
              {!isYearSetupLocked && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowAddHalfDayForm(true); toggleSection('halfDays'); }}
                  className="text-orange-600 hover:text-orange-800 text-sm font-medium"
                >
                  + Add Half Day
                </button>
              )}
            </div>
            {expandedSections.halfDays && <div className="p-4">
              {showAddHalfDayForm && (
                <div className="bg-orange-50 rounded-lg p-3 mb-3 border border-orange-200">
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      type="text"
                      placeholder="Description"
                      className="flex-1 min-w-[120px] px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      value={newHalfDayForm.name}
                      onChange={(e) => setNewHalfDayForm({...newHalfDayForm, name: e.target.value})}
                    />
                    <input
                      type="date"
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      value={newHalfDayForm.date}
                      onChange={(e) => setNewHalfDayForm({...newHalfDayForm, date: e.target.value})}
                    />
                    <select
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      value={newHalfDayForm.period}
                      onChange={(e) => setNewHalfDayForm({...newHalfDayForm, period: e.target.value})}
                    >
                      <option value="afternoon">PM off</option>
                      <option value="morning">AM only</option>
                    </select>
                    <button
                      onClick={async () => {
                        if (newHalfDayForm.name && newHalfDayForm.date) {
                          await onAddSchoolCalendarEvent({
                            type: 'half-day',
                            name: newHalfDayForm.name,
                            startDate: newHalfDayForm.date,
                            endDate: newHalfDayForm.date,
                            halfDayPeriod: newHalfDayForm.period,
                            libraryStatus: 'limited'
                          });
                          if (!savingSchoolCalendarEvent) {
                            setNewHalfDayForm({ name: '', date: '', period: 'afternoon' });
                            setShowAddHalfDayForm(false);
                          }
                        }
                      }}
                      disabled={savingSchoolCalendarEvent}
                      className={`px-3 py-2 rounded-lg text-sm ${savingSchoolCalendarEvent ? 'bg-slate-400 cursor-not-allowed' : 'bg-orange-600 hover:bg-orange-700'} text-white`}
                    >
                      {savingSchoolCalendarEvent ? 'Saving...' : 'Add'}
                    </button>
                    <button
                      onClick={() => { setShowAddHalfDayForm(false); setNewHalfDayForm({ name: '', date: '', period: 'afternoon' }); }}
                      className="text-slate-500 hover:text-slate-700"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}
              {schoolCalendarEvents.filter(e => e.type === 'half-day').length === 0 && !showAddHalfDayForm ? (
                <p className="text-slate-500 text-sm text-center py-4">No half days configured</p>
              ) : (
                <div className="space-y-2">
                  {schoolCalendarEvents.filter(e => e.type === 'half-day').sort((a, b) => new Date(a.startDate) - new Date(b.startDate)).map(h => (
                    editingHalfDay === h.id ? (
                      <div key={h.id} className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                        <div className="flex items-center gap-2 flex-wrap">
                          <input
                            type="text"
                            placeholder="Description"
                            className="flex-1 min-w-[120px] px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            value={editHalfDayForm.name}
                            onChange={(e) => setEditHalfDayForm({...editHalfDayForm, name: e.target.value})}
                          />
                          <input
                            type="date"
                            className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            value={editHalfDayForm.date}
                            onChange={(e) => setEditHalfDayForm({...editHalfDayForm, date: e.target.value})}
                          />
                          <select
                            className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            value={editHalfDayForm.period}
                            onChange={(e) => setEditHalfDayForm({...editHalfDayForm, period: e.target.value})}
                          >
                            <option value="afternoon">PM off</option>
                            <option value="morning">AM only</option>
                          </select>
                          <button
                            onClick={async () => {
                              if (editHalfDayForm.name && editHalfDayForm.date && onEditSchoolCalendarEvent) {
                                await onEditSchoolCalendarEvent(h.id, {
                                  name: editHalfDayForm.name,
                                  startDate: editHalfDayForm.date,
                                  endDate: editHalfDayForm.date,
                                  halfDayPeriod: editHalfDayForm.period
                                });
                                setEditingHalfDay(null);
                              }
                            }}
                            disabled={savingSchoolCalendarEvent}
                            className={`px-3 py-2 rounded-lg text-sm ${savingSchoolCalendarEvent ? 'bg-slate-400 cursor-not-allowed' : 'bg-orange-600 hover:bg-orange-700'} text-white`}
                          >
                            {savingSchoolCalendarEvent ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingHalfDay(null)}
                            className="text-slate-500 hover:text-slate-700"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div key={h.id} className="flex justify-between items-center bg-white rounded-lg px-3 py-2 border border-orange-100 group">
                        <span className="font-medium text-slate-800">{h.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                            {h.halfDayPeriod === 'morning' ? 'AM only' : 'PM off'}
                          </span>
                          <span className="text-slate-500 text-sm">
                            {new Date(h.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                          {!isYearSetupLocked && (
                            <>
                              <button
                                onClick={() => {
                                  setEditHalfDayForm({ name: h.name, date: h.startDate, period: h.halfDayPeriod || 'afternoon' });
                                  setEditingHalfDay(h.id);
                                }}
                                className="text-orange-400 hover:text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                ✎
                              </button>
                              <button
                                onClick={() => onDeleteSchoolCalendarEvent(h.id)}
                                className="text-orange-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                ✕
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )
                  ))}
                </div>
              )}
            </div>}
          </div>

          {/* Library Events & Workdays Section */}
          <div className="border-2 border-teal-200 rounded-xl overflow-hidden">
            <div
              className="bg-teal-50 px-4 py-3 flex justify-between items-center cursor-pointer hover:bg-teal-100 transition-colors"
              onClick={() => toggleSection('libraryEvents')}
            >
              <h4 className="font-bold text-teal-800 flex items-center gap-2">
                <span className={`transition-transform ${expandedSections.libraryEvents ? 'rotate-90' : ''}`}>▶</span>
                <span>📚</span> Library Events & Workdays
                <span className="text-sm font-normal text-teal-600">({schoolCalendarEvents.filter(e => ['workday', 'book-fair', 'tla', 'diocesan-meeting', 'inventory', 'custom'].includes(e.type)).length})</span>
              </h4>
              {!isYearSetupLocked && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowAddEventForm(true); toggleSection('libraryEvents'); }}
                  className="text-teal-600 hover:text-teal-800 text-sm font-medium"
                >
                  + Add Event
                </button>
              )}
            </div>
            {expandedSections.libraryEvents && <div className="p-4">
              {showAddEventForm && (
                <div className="bg-teal-50 rounded-lg p-3 mb-3 border border-teal-200">
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      value={newEventForm.type}
                      onChange={(e) => setNewEventForm({...newEventForm, type: e.target.value})}
                    >
                      <option value="book-fair">Book Fair</option>
                      <option value="tla">TLA/PD</option>
                      <option value="diocesan-meeting">Diocesan Meeting</option>
                      <option value="workday">Workday</option>
                      <option value="inventory">Inventory</option>
                      <option value="custom">Custom</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Event Name"
                      className="flex-1 min-w-[120px] px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      value={newEventForm.name}
                      onChange={(e) => setNewEventForm({...newEventForm, name: e.target.value})}
                    />
                    <input
                      type="date"
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      value={newEventForm.startDate}
                      onChange={(e) => setNewEventForm({...newEventForm, startDate: e.target.value})}
                    />
                    <span className="text-slate-400">to</span>
                    <input
                      type="date"
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      value={newEventForm.endDate}
                      onChange={(e) => setNewEventForm({...newEventForm, endDate: e.target.value})}
                    />
                    <button
                      onClick={async () => {
                        if (newEventForm.name && newEventForm.startDate) {
                          await onAddSchoolCalendarEvent({
                            type: newEventForm.type,
                            name: newEventForm.name,
                            startDate: newEventForm.startDate,
                            endDate: newEventForm.endDate || newEventForm.startDate,
                            libraryStatus: newEventForm.type === 'book-fair' ? 'limited' : 'closed'
                          });
                          if (!savingSchoolCalendarEvent) {
                            setNewEventForm({ type: 'book-fair', name: '', startDate: '', endDate: '', libraryStatus: 'closed' });
                            setShowAddEventForm(false);
                          }
                        }
                      }}
                      disabled={savingSchoolCalendarEvent}
                      className={`px-3 py-2 rounded-lg text-sm ${savingSchoolCalendarEvent ? 'bg-slate-400 cursor-not-allowed' : 'bg-teal-600 hover:bg-teal-700'} text-white`}
                    >
                      {savingSchoolCalendarEvent ? 'Saving...' : 'Add'}
                    </button>
                    <button
                      onClick={() => { setShowAddEventForm(false); setNewEventForm({ type: 'book-fair', name: '', startDate: '', endDate: '', libraryStatus: 'closed' }); }}
                      className="text-slate-500 hover:text-slate-700"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}
              {schoolCalendarEvents.filter(e => ['workday', 'book-fair', 'tla', 'diocesan-meeting', 'inventory', 'custom'].includes(e.type)).length === 0 && !showAddEventForm ? (
                <p className="text-slate-500 text-sm text-center py-4">No library events configured</p>
              ) : (
                <div className="space-y-2">
                  {schoolCalendarEvents.filter(e => ['workday', 'book-fair', 'tla', 'diocesan-meeting', 'inventory', 'custom'].includes(e.type)).sort((a, b) => new Date(a.startDate) - new Date(b.startDate)).map(e => (
                    editingEvent === e.id ? (
                      <div key={e.id} className="bg-teal-50 rounded-lg p-3 border border-teal-200">
                        <div className="flex items-center gap-2 flex-wrap">
                          <select
                            className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            value={editEventForm.type}
                            onChange={(ev) => setEditEventForm({...editEventForm, type: ev.target.value})}
                          >
                            <option value="book-fair">Book Fair</option>
                            <option value="tla">TLA/PD</option>
                            <option value="diocesan-meeting">Diocesan Meeting</option>
                            <option value="workday">Workday</option>
                            <option value="inventory">Inventory</option>
                            <option value="custom">Custom</option>
                          </select>
                          <input
                            type="text"
                            placeholder="Event Name"
                            className="flex-1 min-w-[120px] px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            value={editEventForm.name}
                            onChange={(ev) => setEditEventForm({...editEventForm, name: ev.target.value})}
                          />
                          <input
                            type="date"
                            className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            value={editEventForm.startDate}
                            onChange={(ev) => setEditEventForm({...editEventForm, startDate: ev.target.value})}
                          />
                          <span className="text-slate-400">to</span>
                          <input
                            type="date"
                            className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            value={editEventForm.endDate}
                            onChange={(ev) => setEditEventForm({...editEventForm, endDate: ev.target.value})}
                          />
                          <button
                            onClick={async () => {
                              if (editEventForm.name && editEventForm.startDate && onEditSchoolCalendarEvent) {
                                await onEditSchoolCalendarEvent(e.id, {
                                  type: editEventForm.type,
                                  name: editEventForm.name,
                                  startDate: editEventForm.startDate,
                                  endDate: editEventForm.endDate || editEventForm.startDate,
                                  libraryStatus: editEventForm.type === 'book-fair' ? 'limited' : 'closed'
                                });
                                setEditingEvent(null);
                              }
                            }}
                            disabled={savingSchoolCalendarEvent}
                            className={`px-3 py-2 rounded-lg text-sm ${savingSchoolCalendarEvent ? 'bg-slate-400 cursor-not-allowed' : 'bg-teal-600 hover:bg-teal-700'} text-white`}
                          >
                            {savingSchoolCalendarEvent ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingEvent(null)}
                            className="text-slate-500 hover:text-slate-700"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div key={e.id} className="flex justify-between items-center bg-white rounded-lg px-3 py-2 border border-teal-100 group">
                        <div>
                          <span className="font-medium text-slate-800">{e.name}</span>
                          <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                            e.type === 'book-fair' ? 'bg-yellow-100 text-yellow-700' :
                            e.type === 'tla' ? 'bg-blue-100 text-blue-700' :
                            e.type === 'diocesan-meeting' ? 'bg-purple-100 text-purple-700' :
                            e.type === 'workday' ? 'bg-slate-100 text-slate-700' :
                            e.type === 'inventory' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {e.type === 'book-fair' ? 'Book Fair' :
                             e.type === 'tla' ? 'TLA/PD' :
                             e.type === 'diocesan-meeting' ? 'Diocesan' :
                             e.type === 'workday' ? 'Workday' :
                             e.type === 'inventory' ? 'Inventory' :
                             e.type}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500 text-sm">
                            {e.startDate === e.endDate
                              ? new Date(e.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                              : `${new Date(e.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(e.endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                            }
                          </span>
                          {!isYearSetupLocked && (
                            <>
                              <button
                                onClick={() => {
                                  setEditEventForm({
                                    type: e.type,
                                    name: e.name,
                                    startDate: e.startDate,
                                    endDate: e.endDate,
                                    libraryStatus: e.libraryStatus || 'closed'
                                  });
                                  setEditingEvent(e.id);
                                }}
                                className="text-teal-400 hover:text-teal-600 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                ✎
                              </button>
                              <button
                                onClick={() => onDeleteSchoolCalendarEvent(e.id)}
                                className="text-teal-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                ✕
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )
                  ))}
                </div>
              )}
            </div>}
          </div>

          {/* Saturday Openings Section */}
          <div className="border-2 border-amber-200 rounded-xl overflow-hidden">
            <div
              className="bg-amber-50 px-4 py-3 flex justify-between items-center cursor-pointer hover:bg-amber-100 transition-colors"
              onClick={() => toggleSection('saturdayOpenings')}
            >
              <h4 className="font-bold text-amber-800 flex items-center gap-2">
                <span className={`transition-transform ${expandedSections.saturdayOpenings ? 'rotate-90' : ''}`}>▶</span>
                <span>📅</span> Saturday Library Hours
                <span className="text-sm font-normal text-amber-600">({saturdayOpenings.length})</span>
              </h4>
              {!isYearSetupLocked && saturdaySchedule?.enabled && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowAddSaturdayForm(true); toggleSection('saturdayOpenings'); }}
                  className="text-amber-600 hover:text-amber-800 text-sm font-medium"
                >
                  + Add Saturday
                </button>
              )}
            </div>
            {expandedSections.saturdayOpenings && <div className="p-4">
              {/* Saturday Hours Configuration Banner */}
              {saturdaySchedule?.enabled ? (
                <div className="mb-4 bg-amber-50 rounded-lg p-3 border border-amber-200">
                  <div className="flex items-center gap-2 text-amber-800">
                    <span className="text-lg">⏰</span>
                    <span className="font-medium">
                      Saturday Hours: {formatTime12(saturdaySchedule.startTime || '09:00')} - {formatTime12(saturdaySchedule.endTime || '13:00')}
                    </span>
                  </div>
                  <p className="text-amber-600 text-sm mt-1">Same hours apply to both libraries</p>
                </div>
              ) : (
                <div className="text-center py-6 mb-4">
                  <p className="text-amber-600 mb-4">Saturday hours are not configured yet.</p>
                  {!isYearSetupLocked && !showSaturdayConfigForm && (
                    <button
                      onClick={() => setShowSaturdayConfigForm(true)}
                      className="bg-amber-600 text-white px-6 py-2 rounded-lg hover:bg-amber-700 transition-colors font-medium"
                    >
                      Configure Saturday Hours
                    </button>
                  )}
                  {showSaturdayConfigForm && (
                    <div className="bg-amber-50 rounded-lg p-4 border border-amber-200 mt-4">
                      <h4 className="font-medium text-amber-800 mb-4">Configure Saturday Library Hours</h4>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm text-amber-700 mb-1">Start Time</label>
                            <input
                              type="time"
                              className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:border-amber-500 focus:ring-1 focus:ring-amber-200 outline-none"
                              value={saturdayConfigForm.startTime}
                              onChange={(e) => setSaturdayConfigForm({ ...saturdayConfigForm, startTime: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-amber-700 mb-1">End Time</label>
                            <input
                              type="time"
                              className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:border-amber-500 focus:ring-1 focus:ring-amber-200 outline-none"
                              value={saturdayConfigForm.endTime}
                              onChange={(e) => setSaturdayConfigForm({ ...saturdayConfigForm, endTime: e.target.value })}
                            />
                          </div>
                        </div>
                        <p className="text-sm text-amber-600">These hours will apply to both libraries on all Saturday openings.</p>
                        <div className="flex gap-2">
                          <button
                            onClick={async () => {
                              await updateSaturdaySchedule({
                                enabled: true,
                                startTime: saturdayConfigForm.startTime,
                                endTime: saturdayConfigForm.endTime
                              });
                              setShowSaturdayConfigForm(false);
                            }}
                            className="bg-amber-600 text-white px-6 py-2 rounded-lg hover:bg-amber-700 transition-colors font-medium"
                          >
                            Enable Saturday Hours
                          </button>
                          <button
                            onClick={() => setShowSaturdayConfigForm(false)}
                            className="text-amber-600 hover:text-amber-800 px-4"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Add Saturday Form */}
              {showAddSaturdayForm && saturdaySchedule?.enabled && (
                <div className="bg-amber-50 rounded-lg p-3 mb-3 border border-amber-200">
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      value={newSaturdayForm.date}
                      onChange={(e) => setNewSaturdayForm({...newSaturdayForm, date: e.target.value})}
                    />
                    <input
                      type="text"
                      placeholder="Description (optional)"
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      value={newSaturdayForm.description}
                      onChange={(e) => setNewSaturdayForm({...newSaturdayForm, description: e.target.value})}
                    />
                    <button
                      onClick={async () => {
                        if (newSaturdayForm.date) {
                          await onAddSaturdayOpening(newSaturdayForm.date, newSaturdayForm.description);
                          setNewSaturdayForm({ date: '', description: '' });
                          setShowAddSaturdayForm(false);
                        }
                      }}
                      disabled={savingSchoolCalendarEvent}
                      className={`px-3 py-2 rounded-lg text-sm ${savingSchoolCalendarEvent ? 'bg-slate-400 cursor-not-allowed' : 'bg-amber-600 hover:bg-amber-700'} text-white`}
                    >
                      {savingSchoolCalendarEvent ? 'Saving...' : 'Add'}
                    </button>
                    <button
                      onClick={() => { setShowAddSaturdayForm(false); setNewSaturdayForm({ date: '', description: '' }); }}
                      className="text-slate-500 hover:text-slate-700"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}

              {/* Saturday Dates List */}
              {saturdaySchedule?.enabled && saturdayOpenings.length === 0 && !showAddSaturdayForm ? (
                <p className="text-slate-500 text-sm text-center py-4">No Saturday openings configured</p>
              ) : saturdaySchedule?.enabled ? (
                <div className="space-y-2">
                  {saturdayOpenings.sort((a, b) => new Date(a.date) - new Date(b.date)).map(sat => (
                    editingSaturdayDate === sat.id ? (
                      <div key={sat.id} className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                        <div className="flex items-center gap-2">
                          <input
                            type="date"
                            className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            value={editSaturdayForm.date}
                            onChange={(e) => setEditSaturdayForm({...editSaturdayForm, date: e.target.value})}
                          />
                          <input
                            type="text"
                            placeholder="Description"
                            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            value={editSaturdayForm.description}
                            onChange={(e) => setEditSaturdayForm({...editSaturdayForm, description: e.target.value})}
                          />
                          <button
                            onClick={async () => {
                              if (editSaturdayForm.date && onEditSaturdayOpening) {
                                await onEditSaturdayOpening(sat.id, editSaturdayForm.date, editSaturdayForm.description);
                                setEditingSaturdayDate(null);
                              }
                            }}
                            disabled={savingSchoolCalendarEvent}
                            className={`px-3 py-2 rounded-lg text-sm ${savingSchoolCalendarEvent ? 'bg-slate-400 cursor-not-allowed' : 'bg-amber-600 hover:bg-amber-700'} text-white`}
                          >
                            {savingSchoolCalendarEvent ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingSaturdayDate(null)}
                            className="text-slate-500 hover:text-slate-700"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div key={sat.id} className="group bg-white hover:bg-amber-50 border border-slate-200 rounded-lg p-3 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="font-medium text-slate-700">
                              {new Date(sat.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                            {sat.description && (
                              <span className="text-sm text-amber-600">• {sat.description}</span>
                            )}
                          </div>
                          {!isYearSetupLocked && (
                            <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => {
                                  setEditSaturdayForm({ date: sat.date, description: sat.description || '' });
                                  setEditingSaturdayDate(sat.id);
                                }}
                                className="text-amber-500 hover:text-amber-700 text-sm"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => onDeleteSaturdayOpening(sat.id)}
                                className="text-red-500 hover:text-red-700 text-sm"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  ))}
                </div>
              ) : null}
            </div>}
          </div>
        </div>
      )}
    </div>
  );
}

export default YearSetupTab;
